import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult
} from '@tanstack/react-query';

import { ServiceObject, ServiceSearchData } from '../@types';
import { InterceptedFormsObject } from '../@types/form-builder.type';
import {
  GetParameterResponseType,
  InteractionsPartRequestFormInitialPartType,
  InteractionsPartRequestFormPartQty,
  InteractionsPartRequestFormPartResponse,
  TemplateListResponse,
  TemplateRowDataType,
  TriggerPoint,
  queryOptionType
} from '../@types/interactions.type';
import { Folders } from '../@types/uploaded-files.type';
import { HttpMethod } from '../api/http-client';
import {
  createInteractionTemplate,
  getAllInteractionTemplates,
  getInteractionsPartRequestFormPartQuantities,
  getInteractionsPartRequestFormPartTypes,
  getInteractionsPartRequestFormPartTypesChildrenWithParent,
  updateInteractionTemplate,
  submitFormsDataWithOriginalPayload,
  getParameterData,
  getServiceSearchData,
  downloadNmiCertificate,
  editInteraction,
  modifyStructuresToggleActive,
  getTriggerPointTypes,
  EditInteractionData,
  EditInteractionResponse,
  NmiCertificateRequest,
  NmiCertificateResponse,
  ModifyStructuresToggleActiveData,
} from '../api/interactions';
import {
  InteractionTemplateKey, 
  InteractionsGetParameterFormParameterDataKey, 
  InteractionsPartRequestFormPartQuantitiesKey, 
  InteractionsPartRequestFormPartTypesChildrenWithParentKey,
  InteractionsPartRequestFormPartTypesKey,
  InteractionsServiceSearchFormDataKey,
  TriggerPointKey
} from '../configs/interactions';
import { PendingInteractionKey } from '../configs/pendingInteraction';
import { ServiceTypeKey } from '../configs/service-type';
import { 
  CheckInKey, 
  GetServiceKey, 
  ServiceCheckInKey, 
  ServiceLogAllKey 
} from '../configs/services';
import { UploadedFilesKey } from '../configs/uploaded-files';

export const useGetAllInteractionTemplates = (queryOptions: queryOptionType): UseQueryResult<TemplateListResponse> => {
  return useQuery({
    queryKey: [InteractionTemplateKey, queryOptions.filterModel],
    queryFn: () => getAllInteractionTemplates(queryOptions),
    enabled: false,
    gcTime: 0
  });
};

export const useAddInteractionTemplate = (): UseMutationResult<TemplateRowDataType, Error, TemplateRowDataType> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TemplateRowDataType) => createInteractionTemplate(data),

    onSuccess: async (response: TemplateRowDataType) => {
      await queryClient.invalidateQueries({
        queryKey: [InteractionTemplateKey, response.id]
      });
      await queryClient.invalidateQueries({
        queryKey: [InteractionTemplateKey]
      });
    }
  });
};

export const useUpdateInteractionTemplate = (id?: number): UseMutationResult<TemplateRowDataType, Error, TemplateRowDataType> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TemplateRowDataType) => updateInteractionTemplate(data.id || 0, data),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [InteractionTemplateKey]
      });
      await queryClient.invalidateQueries({
        queryKey: [InteractionTemplateKey, id]
      });
    }
  });
};

// InterceptedFormsObject.payload is intentionally `any` - it varies per API endpoint called dynamically
export const useSubmitFormsDataWithOriginalPayload = (id: number): UseMutationResult<unknown, Error, InterceptedFormsObject> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: InterceptedFormsObject) => {
      return submitFormsDataWithOriginalPayload(data.url, data.method as HttpMethod, {
        ...data.payload,
        interactionForms: data.forms || [],
        actionTriggers: data.actionTriggers || []
      });
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceTypeKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [GetServiceKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceCheckInKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [CheckInKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [PendingInteractionKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [UploadedFilesKey, id, Folders.ServiceAttachments]
      });
    }
  });
};

export const useGetInteractionsPartRequestFormPartTypes = (serviceId: number, conditionCode: string): UseQueryResult<Array<InteractionsPartRequestFormInitialPartType>> => {
  return useQuery({
    queryKey: [InteractionsPartRequestFormPartTypesKey, serviceId, conditionCode],
    queryFn: () => getInteractionsPartRequestFormPartTypes(serviceId, conditionCode)
  });
};

export const useGetInteractionsPartRequestFormPartTypesChildrenWithParent = (partTypeId: number): UseQueryResult<InteractionsPartRequestFormPartResponse | undefined> => {
  return useQuery({
    queryKey: [InteractionsPartRequestFormPartTypesChildrenWithParentKey, partTypeId],
    queryFn: () => getInteractionsPartRequestFormPartTypesChildrenWithParent(partTypeId),
    enabled: partTypeId !== -1
  });
};

export const useGetInteractionsPartRequestFormPartQuantities = (partTypeId: number, serviceId: number, serialised: boolean): UseQueryResult<InteractionsPartRequestFormPartQty | undefined> => {
  return useQuery({
    queryKey: [InteractionsPartRequestFormPartQuantitiesKey, partTypeId, serviceId],
    queryFn: () => getInteractionsPartRequestFormPartQuantities(partTypeId, serviceId, serialised),
    enabled: partTypeId !== -1
  });
};

export const useGetParameterData = (serviceId: number, selectedParameter: string, mainLocationNodeId: number, subLocationNodeId: number, clientId: number): UseQueryResult<GetParameterResponseType | undefined> => {
  return useQuery({
    queryKey: [InteractionsGetParameterFormParameterDataKey, serviceId, selectedParameter],
    queryFn: () => getParameterData(serviceId, selectedParameter, mainLocationNodeId, subLocationNodeId, clientId),
    enabled: !!selectedParameter
  });
};

export const useGetServiceSearchData = (serviceId: number, serviceSearchData: ServiceSearchData): UseQueryResult<ServiceObject[] | undefined> => {
  return useQuery({
    queryKey: [InteractionsServiceSearchFormDataKey, serviceId],
    queryFn: () => getServiceSearchData(serviceId, serviceSearchData),
    enabled: !!serviceSearchData
  });
};

export const useDownloadNmiCertificate = (): UseMutationResult<NmiCertificateResponse, Error, NmiCertificateRequest> => {
  return useMutation({
    mutationFn: (data: NmiCertificateRequest) => downloadNmiCertificate(data),
  });
};

export const useEditInteraction = (): UseMutationResult<EditInteractionResponse, Error, EditInteractionData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: EditInteractionData) => {
      return await editInteraction(data);
    },

    onSuccess: async (response: EditInteractionResponse) => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey, response.serviceId]
      });
    }
  });
};

export const useModifyStructuresToggleActive = (): UseMutationResult<void, Error, ModifyStructuresToggleActiveData> => {
  return useMutation({
    mutationFn: (data: ModifyStructuresToggleActiveData) => modifyStructuresToggleActive(data),
  });
};

export const useGetTriggerPointTypes = (): UseQueryResult<TriggerPoint[] | undefined> => {
  return useQuery({
    queryKey: [TriggerPointKey],
    queryFn: () => getTriggerPointTypes()
  });
};